import { type FC, useState, useCallback, useRef } from 'react';
import { BsCloudArrowUp } from 'react-icons/bs';

interface Props {
	/** Called with the validated files the user selected or dropped. */
	onFilesSelected: (files: File[]) => void;
}

const UploadArea: FC<Props> = ({ onFilesSelected }) => {
	const [isDragOver, setIsDragOver] = useState(false);
	const [stageError, setStageError] = useState<string | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const handleFiles = useCallback(
		(fileList: FileList) => {
			setStageError(null);
			const allowedExtensions = ['.pptx', '.ppt', '.pdf'];
			const maxSizeBytes = 500 * 1024 * 1024; // 500MB
			const valid: File[] = [];
			const rejected: File[] = [];
			const oversized: File[] = [];
			for (const file of Array.from(fileList)) {
				if (!allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))) {
					rejected.push(file);
				} else if (file.size > maxSizeBytes) {
					oversized.push(file);
				} else {
					valid.push(file);
				}
			}
			if (rejected.length > 0) {
				setStageError('Only PPTX, PPT, or PDF files are accepted.');
				return;
			}
			if (oversized.length > 0) {
				const names = oversized.map((f) => f.name).join(', ');
				setStageError(
					`The following file(s) exceed the 500MB size limit and cannot be uploaded: ${names}`
				);
				return;
			}
			if (valid.length > 0) {
				onFilesSelected(valid);
			}
		},
		[onFilesSelected]
	);

	const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(false);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			setIsDragOver(false);
			if (e.dataTransfer.files.length > 0) {
				handleFiles(e.dataTransfer.files);
			}
		},
		[handleFiles]
	);

	const handleInputChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.files?.length) {
				handleFiles(e.target.files);
				// Reset so the same file can be re-selected after removal
				e.target.value = '';
			}
		},
		[handleFiles]
	);

	return (
		<div className="uploadArea">
			<div
				className={`dropZone${isDragOver ? ' isDragOver' : ''}`}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onClick={() => fileInputRef.current?.click()}
				role="button"
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
				}}
				aria-label="Add lecture slides – drag & drop or click to browse">
				<input
					ref={fileInputRef}
					type="file"
					multiple
					accept=".pptx,.ppt,.pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-powerpoint,application/pdf"
					style={{ display: 'none' }}
					onChange={handleInputChange}
					tabIndex={-1}
					aria-hidden="true"
				/>
				<BsCloudArrowUp className="uploadIcon" />
				<p className="uploadHintPrimary">Click to upload or drag and drop new slides</p>
				<p className="uploadHintSecondary">PPTX, PPT, or PDF (max. 500MB)</p>
			</div>
			{stageError && (
				<p className="uploadError" role="alert">
					{stageError}
				</p>
			)}
		</div>
	);
};

export default UploadArea;
